// ffmpeg.wasm-based trim + convert.
//
// Two execution modes:
//  - ffmpegRemux: stream-copy (-c copy). ~20-100x faster, ~10x less memory,
//    snaps start to nearest preceding keyframe. Default path.
//  - ffmpegTrimToMp4: full re-encode (libx264 + aac). Frame-accurate, slow.
//    Used as fallback when remux fails or produces invalid output.
//
// The @ffmpeg/ffmpeg v0.12 FFmpeg class already runs the wasm inside its
// own internal Web Worker, so the heavy work is off the main thread.

import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile, toBlobURL } from "@ffmpeg/util";
import * as Sentry from "@sentry/react";
import { devLog, logError } from "./logger";

let ffmpegInstance: FFmpeg | null = null;
let loadPromise: Promise<FFmpeg> | null = null;

const LOAD_TIMEOUT_MS = 60000;

export class FFmpegLoadTimeoutError extends Error {
  constructor() {
    super("ffmpeg core load timed out");
    this.name = "FFmpegLoadTimeoutError";
  }
}

const CORE_VERSION = "0.12.10";
const CORE_BASE = `https://cdn.jsdelivr.net/npm/@ffmpeg/core@${CORE_VERSION}/dist/esm`;

async function getFFmpeg(onProgress?: (msg: string) => void): Promise<FFmpeg> {
  if (ffmpegInstance) return ffmpegInstance;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    try {
      const ffmpeg = new FFmpeg();
      ffmpeg.on("log", ({ message }) => {
        devLog("[ffmpeg]", message);
      });
      onProgress?.("Loading converter…");

      const loadWork = (async () => {
        const [coreURL, wasmURL] = await Promise.all([
          toBlobURL(`${CORE_BASE}/ffmpeg-core.js`, "text/javascript"),
          toBlobURL(`${CORE_BASE}/ffmpeg-core.wasm`, "application/wasm"),
        ]);
        await ffmpeg.load({ coreURL, wasmURL });
      })();

      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new FFmpegLoadTimeoutError()), LOAD_TIMEOUT_MS);
      });

      try {
        await Promise.race([loadWork, timeout]);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }

      devLog("[ffmpeg] core loaded");
      ffmpegInstance = ffmpeg;
      return ffmpeg;
    } catch (err) {
      if (err instanceof FFmpegLoadTimeoutError) {
        Sentry.captureMessage("ffmpeg wasm core load timed out after 60s", "warning");
      } else {
        logError("ffmpeg", err);
      }
      loadPromise = null; // allow retry on next call
      throw err;
    }
  })();

  return loadPromise;
}

/**
 * Warm up the ffmpeg core during idle time so the first export feels instant.
 * Safe to call repeatedly — subsequent calls reuse the cached instance.
 */
export function preloadFFmpeg(): void {
  if (ffmpegInstance || loadPromise) return;
  // Fire-and-forget; swallow errors (we'll retry on real use).
  getFFmpeg().catch(() => {
    /* noop */
  });
}

function pickInputName(file: File | Blob): string {
  const raw = (file as File).name?.toLowerCase() || "";
  const ext = raw.split(".").pop();
  const safe = ext && /^[a-z0-9]{2,4}$/.test(ext) ? ext : "mov";
  return `input.${safe}`;
}

async function readOut(ffmpeg: FFmpeg, name: string): Promise<Blob> {
  const data = await ffmpeg.readFile(name);
  const bytes =
    data instanceof Uint8Array
      ? new Uint8Array(data)
      : new TextEncoder().encode(String(data));
  return new Blob([bytes.buffer as ArrayBuffer], { type: "video/mp4" });
}

/**
 * Stream-copy trim — fast remux, no re-encode. Snaps to nearest keyframe.
 */
export async function ffmpegRemux(
  file: File | Blob,
  trimStart: number,
  trimEnd: number,
  onProgress?: (msg: string) => void,
): Promise<Blob> {
  if (trimEnd <= trimStart) {
    return file instanceof Blob ? file : new Blob([file]);
  }

  const ffmpeg = await getFFmpeg(onProgress);
  const inputName = pickInputName(file);
  const outputName = "remux.mp4";
  const duration = Math.max(0, trimEnd - trimStart);

  onProgress?.("Trimming…");
  await ffmpeg.writeFile(inputName, await fetchFile(file));

  // -ss before -i = fast seek (lands on prior keyframe). -c copy = no re-encode.
  await ffmpeg.exec([
    "-ss", trimStart.toFixed(3),
    "-i", inputName,
    "-t", duration.toFixed(3),
    "-map", "0:v:0",
    "-map", "0:a:0?",
    "-c", "copy",
    "-movflags", "+faststart",
    "-avoid_negative_ts", "make_zero",
    outputName,
  ]);

  const out = await readOut(ffmpeg, outputName);
  try {
    await ffmpeg.deleteFile(inputName);
    await ffmpeg.deleteFile(outputName);
  } catch {
    /* noop */
  }
  return out;
}

/**
 * Full re-encode — frame-accurate, slow, high CPU/memory. Fallback only.
 */
export async function ffmpegTrimToMp4(
  file: File | Blob,
  trimStart: number,
  trimEnd: number,
  onProgress?: (msg: string) => void,
): Promise<Blob> {
  if (trimEnd <= trimStart) {
    return file instanceof Blob ? file : new Blob([file]);
  }

  const ffmpeg = await getFFmpeg(onProgress);
  const inputName = pickInputName(file);
  const outputName = "output.mp4";
  const duration = Math.max(0, trimEnd - trimStart);

  onProgress?.("Re-encoding video…");
  await ffmpeg.writeFile(inputName, await fetchFile(file));

  await ffmpeg.exec([
    "-ss", trimStart.toFixed(3),
    "-i", inputName,
    "-t", duration.toFixed(3),
    "-c:v", "libx264",
    "-preset", "fast",
    "-crf", "23",
    "-c:a", "aac",
    "-b:a", "128k",
    "-movflags", "+faststart",
    outputName,
  ]);

  const out = await readOut(ffmpeg, outputName);
  try {
    await ffmpeg.deleteFile(inputName);
    await ffmpeg.deleteFile(outputName);
  } catch {
    /* noop */
  }
  return out;
}
